import * as React from "react";
import { ChevronRight } from "lucide-react";
import { cn } from "@/shared/lib/utils";
import {
  Breadcrumb as BreadcrumbRoot,
  BreadcrumbList,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/shared/ui/components/ui/breadcrumb";
import { cva, type VariantProps } from "class-variance-authority";
import homeIcon from "@/assets/svgs/homeIcon.svg";

const breadcrumbVariants = cva("", {
  variants: {
    theme: {
      default: "bg-background",
      dark: "bg-gray-100 rounded-lg",
    },
  },
  defaultVariants: {
    theme: "default",
  },
});

export interface BreadcrumbItemType {
  label: string;
  href?: string;
  isActive?: boolean;
}

export interface BreadcrumbProps
  extends React.ComponentPropsWithoutRef<typeof BreadcrumbRoot>,
    VariantProps<typeof breadcrumbVariants> {
  items: BreadcrumbItemType[];
  separator?: React.ReactNode;
  className?: string;
  containerClassName?: string;
}

const Breadcrumb = React.forwardRef<HTMLElement, BreadcrumbProps>(
  (
    {
      items,
      separator,
      theme = "default",
      className,
      containerClassName,
      ...props
    },
    ref
  ) => {
    if (!items || items.length === 0) {
      return null;
    }

    return (
      <div
        className={cn(
          "w-fit rounded-md px-4 py-2",
          breadcrumbVariants({ theme }),
          containerClassName
        )}
      >
        <BreadcrumbRoot ref={ref} className={className} {...props}>
          <BreadcrumbList>
            {items.map((item, index) => {
              const isLast = index === items.length - 1;
              const isActive = item.isActive ?? isLast;
              const isHome = item.label.toLowerCase() === "home";
              const isLastSeparator = index === items.length - 2;

              return (
                <React.Fragment key={index}>
                  <BreadcrumbItem>
                    {isActive || !item.href ? (
                      <BreadcrumbPage className="text-xxs font-medium text-gray-400 flex items-center gap-1">
                        {isHome && (
                          <img
                            width={16}
                            height={16}
                            src={homeIcon}
                            alt="Home"
                          />
                        )}
                        {item.label}
                      </BreadcrumbPage>
                    ) : (
                      <BreadcrumbLink
                        href={item.href}
                        className="text-xxs font-medium text-gray-900 flex items-center gap-1"
                      >
                        {isHome && (
                          <img
                            width={16}
                            height={16}
                            src={homeIcon}
                            alt="Home"
                          />
                        )}
                        {item.label}
                      </BreadcrumbLink>
                    )}
                  </BreadcrumbItem>
                  {!isLast && (
                    <BreadcrumbSeparator
                      className={
                        isLastSeparator ? "text-gray-400" : "text-gray-900"
                      }
                    >
                      {separator ?? <ChevronRight />}
                    </BreadcrumbSeparator>
                  )}
                </React.Fragment>
              );
            })}
          </BreadcrumbList>
        </BreadcrumbRoot>
      </div>
    );
  }
);

Breadcrumb.displayName = "Breadcrumb";

export { Breadcrumb, breadcrumbVariants };
